/**
 * The host's declaration about itself (D231; Calder's P7 §3), and the one
 * question the engine asks of it: how saving works HERE.
 *
 * @remarks
 * `saveAutomatic` / `loadAutomatic` ("saves itself after every turn… it will
 * be here") are true of the player app and false in every hosted terminal —
 * `createTerminalSession` keeps its state in a closure, and the 404 pages
 * and the marketing embed store nothing. Three packs had each overridden
 * both slots in their own voice to say so, which was right only while those
 * packs were hosted nowhere else: a pack that knows where it is hosted is a
 * pack that lies the day it is hosted elsewhere. The honest party is the
 * host, so the host says it, once, on the bundle it hands the engine — and
 * the engine's own line is honest in every pack that host runs.
 *
 * The declaration rides the BUNDLE rather than a session option because the
 * pipeline is `(bundle, state, input)` and nothing else (architecture.md
 * §3.3): a replay reproduces the answer from the same three. A third
 * `manifest.saves` mode was the other shape and was refused for the reason
 * above — the manifest is the pack's, and this fact is not the pack's.
 *
 * @packageDocumentation
 */

import type { SaveMode } from '@orpheus/content-schema';

import type { EngineGameBundle } from './engine-game-bundle.js';

/**
 * How saving works for this bundle in this host: the pack's declared mode,
 * unless the host keeps nothing — then `'none'`, whatever the pack said.
 */
export type EffectiveSaveMode = SaveMode | 'none';

/**
 * The bundle as a host that keeps nothing hands it to the engine.
 *
 * @param bundle - A loaded bundle
 * @returns The same bundle, declaring its host keeps nothing
 */
export const hostedKeepingNothing = <Bundle extends EngineGameBundle>(bundle: Bundle): Bundle => ({ ...bundle, host: { keepsNothing: true }});

/**
 * How saving works here — the one read every save-shaped answer makes.
 *
 * @param bundle - The loaded bundle, with or without a host declaration
 * @returns `'none'` when the host keeps nothing; otherwise the pack's mode
 */
export const effectiveSaveModeOf = (bundle: EngineGameBundle): EffectiveSaveMode =>
	bundle.host?.keepsNothing === true ? 'none' : bundle.contentPack.manifest.saves;
